// todo social auth
// todo remember me checkbox
// todo forgot you password link

import type { FC } from 'react'
import React, { useState } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import type { MediaItem } from '@homeviews/models'

import {
  Typography,
  Button,
  TextField,
  Select,
  Snackbar,
} from '@homeviews/shared-ui'
import { Draggable } from '@homeviews/utils'
import MediaCore from '../MediaCore'
import HorizontalCard from '../HorizontalCard'
import { getUser, useRequestNewMediaMutation } from '@homeviews/store'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    separator: {},
    formFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      marginBottom: theme.spacing(2.5),
    },
    radios: {},
    cancelButton: {
      marginRight: 6,
    },
    alertText: {
      display: 'flex',
      alignItems: 'flex-start',
      margin: '20px 0',
      '& img': {
        marginRight: 10,
        flexShrink: 0,
        width: 15,
        marginTop: 2,
      },
    },
    title: {
      marginBottom: 15,
      marginTop: -20,
    },
    subtitle: {
      marginBottom: 20,
    },
    filesUploadField: {},
    filesUploadFieldHeader: {
      marginBottom: 20,
      display: 'flex',
      alignItems: 'center',
      '& h3': {
        marginRight: 10,
      },
    },
    filesUploadFieldButtonWrapper: {},
  }),
)

const formInitialValue = {
  primary_contact: '',
  title: '',
  type: '',
  additional_notes: '',
  images: [],
  files: [],
}

interface IForm {
  primary_contact: string
  title: string
  type: string
  additional_notes: string
  images: any[]
  files: any[]
}

interface IProps {
  onClose?: () => void
}

const RequestMediaForm: FC<IProps> = ({ onClose }) => {
  const classes = useStyles()

  const [loading, setLoading] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [mediaLibraryOpened, setMediaLibraryOpened] = useState<boolean>(false)
  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })
  const user = useSelector(getUser)

  const handleAddImagesClick = () => {
    setMediaLibraryOpened(true)
  }

  const [requestMedia] = useRequestNewMediaMutation()

  const onSubmit = async (values: IForm) => {
    setLoading(true)

    const payload = {
      primary_contact: values.primary_contact,
      notes: values.additional_notes,
      type: values.type,
      title: values.title,
      'files[]': [],
    }

    payload['files[]'] = values.images.map((image: MediaItem) => image?.id)

    try {
      await requestMedia({
        developerId: user?.developer_id,
        payload,
      }).unwrap()
      setSnackbarVisible({
        open: true,
        message: 'Request sent',
      })
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (err) {
      console.log(err)
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  return (
    <div className={classes.root}>
      <Typography variant="h1" className={classes.title}>
        Request new media
      </Typography>
      <Typography className={classes.subtitle}>
        The HomeViews team would be delighted to support you in creating assets
        for your channels. These can include: social media content, blogs,
        portal banner concepts, print materials and website designs.
      </Typography>
      <Formik
        initialValues={formInitialValue}
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          primary_contact: Yup.string().required('Contact field is required'),
          title: Yup.string(),
          type: Yup.string(),
          additional_notes: Yup.string(),
        })}
      >
        {(props: FormikProps<IForm>) => {
          const {
            values,
            touched,
            errors,
            handleBlur,
            handleChange,
            setFieldValue,
          } = props

          const handleSelectImages = (selectedImages) => {
            setMediaLibraryOpened(false)
            setFieldValue('images', selectedImages)
          }

          const handleSelectChange =
            (type: string) =>
            (option: { value: string | number; label: 'string' }) => {
              setFieldValue(type, option?.value || '')
            }

          return (
            <Form>
              <div className="l-fieldWrapper">
                <TextField
                  label="Primary contact (used for feedback with HomeViews)"
                  value={values.primary_contact}
                  data-id="t-primary_contact"
                  id="primary_contact"
                  error={
                    errors.primary_contact && touched.primary_contact
                      ? true
                      : false
                  }
                  helperText={errors.primary_contact}
                  onChange={(e) => {
                    handleChange(e)
                    setFormError('')
                  }}
                  onBlur={handleBlur}
                  fullWidth
                />
              </div>

              <TextField
                label="Title of requested content"
                value={values.title}
                data-id="t-title"
                id="title"
                error={errors.title && touched.title ? true : false}
                helperText={errors.title}
                onChange={(e) => {
                  handleChange(e)
                  setFormError('')
                }}
                onBlur={handleBlur}
                fullWidth
              />
              <div className="l-fieldWrapper">
                <Select
                  label="Type of content required"
                  value={values.type}
                  options={[
                    {
                      label: 'One page summary of reviews',
                      value: 'One page summary of reviews',
                    },
                    {
                      label: 'Social media',
                      value: 'Social media',
                    },
                    {
                      label: 'Portal design concepts',
                      value: 'Portal design concepts',
                    },
                    {
                      label: 'Blog',
                      value: 'Blog',
                    },
                    {
                      label: 'Website designs',
                      value: 'Website designs',
                    },
                    {
                      label: 'Other',
                      value: 'Other',
                    },
                  ]}
                  onChange={handleSelectChange('type')}
                />
              </div>

              <TextField
                label="Additional notes"
                value={values.additional_notes}
                data-id="t-additional_notes"
                id="additional_notes"
                error={
                  errors.additional_notes && touched.additional_notes
                    ? true
                    : false
                }
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={errors.additional_notes}
                name="additional_notes"
                fullWidth
                multiline
                rows={6}
              />

              <div className={classes.filesUploadField}>
                <div className={classes.filesUploadFieldHeader}>
                  <Typography variant="h3">Files</Typography>
                  <div className={classes.filesUploadFieldButtonWrapper}>
                    <Button
                      variant="outlined"
                      customVariant="outlinedPrimary"
                      fullWidth
                      onClick={handleAddImagesClick}
                      size="small"
                      className="l-contentContent__actionButton"
                    >
                      Add new media
                    </Button>
                  </div>
                </div>
                <Draggable
                  list={values.images}
                  updateList={(newList: any) => {
                    setFieldValue('images', newList)
                  }}
                  DraggableChild={HorizontalCard}
                />
              </div>

              <Typography className={classes.alertText}>
                <img
                  src="assets/icons/medium/alert-primary_v2.svg"
                  alt="Info icon"
                />
                Once submitted one of the team will be in touch via email to
                discuss your request within 1 working day.
              </Typography>
              <div className={classes.formFooter}>
                <Button
                  variant="outlined"
                  customColor="dark"
                  onClick={() => {
                    if (!onClose) {
                      return
                    }
                    onClose()
                  }}
                  className={classes.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  customColor="primary"
                  isLoading={loading}
                >
                  Send request
                </Button>
              </div>

              {formError && (
                <Typography className="l-errorMessage">{formError}</Typography>
              )}
              {mediaLibraryOpened && (
                <MediaCore
                  libraryType="images"
                  header="Select from your media"
                  formats="png, jpg, jpeg, svg, gif"
                  selected={values.images || []}
                  onClose={() => {
                    setMediaLibraryOpened(false)
                  }}
                  handleUseSelected={handleSelectImages}
                  openInWindow
                  selectable
                  selectLimit={10}
                />
              )}
            </Form>
          )
        }}
      </Formik>
      <Snackbar
        open={snackbarVisible?.open}
        onClose={(e, reason) => {
          handleSnackbarClose(e, reason)
        }}
        alertMessage={snackbarVisible?.message}
        status={snackbarVisible?.status}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      />
    </div>
  )
}

export default RequestMediaForm
